export default () => {
    self.addEventListener(
        "message",
        function (e) {
            if (e.data.source !== "react-devtools-detector") {
                // Clear caches from previous run
                self.operations = {};
                self._cafBruteCache = {};
                self._selfFinancingCache = {};
                self._orderAccountsCache = {};
                self._operationAccountsCache = {};

                let data = self._buildData(e.data);
                data.uid = e.data.uid;
                self.postMessage(data);
            }
        },
        false
    );

    // ─── Lookup‑map builders (O(n) once instead of O(n) per access) ───

    self._buildLoanMap = (loans) => {
        const map = new Map();
        for (const l of loans) {
            if (!map.has(l.year)) {
                map.set(l.year, l);
            }
        }
        return map;
    };

    self._buildPpiYearMap = (ppiYears) => {
        const map = new Map();
        for (const y of ppiYears) {
            if (!map.has(y.year)) {
                map.set(y.year, y);
            }
        }
        return map;
    };

    self._buildCrdYearMap = (la) => {
        const map = new Map();
        if (la.localAuthorityDebtSimplifiedYears) {
            for (const c of la.localAuthorityDebtSimplifiedYears) {
                if (!map.has(c.year)) {
                    map.set(c.year, c);
                }
            }
        }
        return map;
    };

    self._buildObservationMap = (observations) => {
        const map = new Map();
        for (const o of observations) {
            const key = `${o.year}-${o.account.id}`;
            if (!map.has(key)) {
                map.set(key, o);
            }
        }
        return map;
    };

    // Build a flat list of all visible, priority-matching projects once
    self._buildFilteredProjects = (ppi, priorities) => {
        const projects = [];
        const noPriorityFilter = priorities.length === 0;
        for (const t of ppi.investment_themes) {
            for (const p of t.projects) {
                if (p.visible && (noPriorityFilter || priorities.indexOf(p.priority) !== -1)) {
                    projects.push(p);
                }
            }
            for (const st of t.themes) {
                for (const p of st.projects) {
                    if (p.visible && (noPriorityFilter || priorities.indexOf(p.priority) !== -1)) {
                        projects.push(p);
                    }
                }
            }
        }
        return projects;
    };

    // Build year→amount maps for each project's years and grants
    self._buildProjectYearMaps = (projects) => {
        for (const p of projects) {
            if (!p._yearMap) {
                p._yearMap = new Map();
                for (const y of p.years) {
                    if (!p._yearMap.has(y.year)) {
                        p._yearMap.set(y.year, y);
                    }
                }
            }
            if (!p._grantYearMaps) {
                p._grantYearMaps = [];
                for (const g of p.grants) {
                    const gMap = new Map();
                    for (const gy of g.years) {
                        if (!gMap.has(gy.year)) {
                            gMap.set(gy.year, gy);
                        }
                    }
                    p._grantYearMaps.push({ grant: g, yearMap: gMap });
                }
            }
        }
    };

    // Build year→value maps for revenues/expenses
    self._buildRevenueExpenseMaps = (items) => {
        for (const r of items) {
            if (!r._yearMap) {
                r._yearMap = new Map();
                for (const y of r.years) {
                    if (!r._yearMap.has(y.year)) {
                        r._yearMap.set(y.year, y);
                    }
                }
            }
        }
    };

    // ─── Pre-compute per-year aggregates in a single pass ───

    self._precomputeYearAggregates = (filteredYears, ppi, filteredProjects, loanMap) => {
        const cache = new Map();

        for (const year of filteredYears) {
            const yr = year.year;
            let totalInvests = 0;
            let totalInvestsFctva = 0;
            let grantsAwarded = 0;
            let grantsAsked = 0;

            for (const p of filteredProjects) {
                const py = p._yearMap.get(yr);
                const amount = py ? py.amount : 0;
                totalInvests += amount;
                if (p.fctva) totalInvestsFctva += amount;

                for (const gEntry of p._grantYearMaps) {
                    const gy = gEntry.yearMap.get(yr);
                    if (gy && gy.amount) {
                        if (gEntry.grant.awarded) {
                            grantsAwarded += gy.amount;
                        } else {
                            grantsAsked += gy.amount;
                        }
                    }
                }
            }

            cache.set(yr, {
                totalInvests,
                totalInvestsFctva,
                grantsAwarded,
                grantsAsked,
            });
        }
        return cache;
    };

    // ─── Core computation functions (using caches) ───

    self.reduceTotalInvestsCached = (yearNum, yearCache) => {
        return yearCache.get(yearNum) ? yearCache.get(yearNum).totalInvests : 0;
    };

    self.reduceTotalInvestsFctvaCached = (yearNum, yearCache) => {
        return yearCache.get(yearNum) ? yearCache.get(yearNum).totalInvestsFctva : 0;
    };

    self.aggregateGrantsCached = (awarded, yearNum, yearCache) => {
        const c = yearCache.get(yearNum);
        if (!c) return 0;
        return awarded ? c.grantsAwarded : c.grantsAsked;
    };

    self.reduceFctvaCached = (year, ppi, ppiYearMap, yearCache) => {
        var result = 0;
        for (const y of ppi.years) {
            let fctvaRetrieve = y.fctvaRetrieve;
            if (fctvaRetrieve == null || fctvaRetrieve == undefined) {
                fctvaRetrieve = ppi.fctvaRetrieve;
            }
            if (fctvaRetrieve + y.year !== year.year) continue;

            let fctva;
            if (y.fctvaRetrieve === 0) {
                const py = ppiYearMap.get(year.year);
                fctva = py ? py.fctva / 100 : null;
                if (fctva == null || fctva == undefined) fctva = ppi.fctva / 100;
                if (fctva === null || fctva === undefined || fctva === 0) fctva = 0.164;
                result += reduceTotalInvestsFctvaCached(year.year, yearCache) * fctva;
            } else {
                const py = ppiYearMap.get(y.year);
                fctva = py ? py.fctva / 100 : null;
                if (fctva == null || fctva == undefined) fctva = ppi.fctva / 100;
                if (fctva === null || fctva === undefined || fctva === 0) fctva = 0.164;
                result += reduceTotalInvestsFctvaCached(y.year, yearCache) * fctva;
            }
        }
        return result;
    };

    self.reducePreviousSurplusCached = (year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache) => {
        const prevYear = ppiYearMap.get(year.year - 1);
        if (prevYear) {
            if (prevYear.investResult != null)
                return prevYear.investResult > 0 ? prevYear.investResult : 0;
        }
        if (ppi.years[0].year == year.year) {
            return ppi.years[0].previousSurplus || 0;
        } else {
            var result = 0;
            if (prevYear) {
                result = computeTotalPpiForYear(prevYear, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache);
            }
            return result > 0 ? result : 0;
        }
    };

    self.reduceDeficitRecoveryCached = (year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache) => {
        const prevYear = ppiYearMap.get(year.year - 1);
        if (prevYear) {
            if (prevYear.investResult != null)
                return prevYear.investResult < 0 ? Math.abs(prevYear.investResult) : 0;
        }
        if (ppi.years[0].year == year.year) {
            return ppi.years[0].deficitRecovery || 0;
        } else {
            var result = 0;
            if (prevYear) {
                result = computeTotalPpiForYear(prevYear, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache);
            }
            return result < 0 ? result * -1 : 0;
        }
    };

    self.computeTotalRevenueForYear = (year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache) => {
        const yr = year.year;
        var result = 0;

        for (const r of ppi.revenues) {
            const ry = r._yearMap.get(yr);
            if (ry) result += ry.value;
        }

        result += year.saleOfHeritage || 0;
        result += year.tpoRevenue || 0;
        result += year.planningFee || 0;

        const selfFinancing = getCachedSelfFinancing(
            year,
            ppi,
            la._sourceLoans,
            [],
            current_year,
            scenario,
            la
        );

        result += selfFinancing || 0;
        result += reducePreviousSurplusCached(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache);
        result += reduceFctvaCached(year, ppi, ppiYearMap, yearCache);
        result += aggregateGrantsCached(true, yr, yearCache);
        result += aggregateGrantsCached(false, yr, yearCache);

        const loan = loanMap.get(yr);
        if (loan) result += loan.total;

        return result;
    };

    self.computeTotalSpendingForYear = (year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache) => {
        const yr = year.year;
        var result = 0;

        for (const r of ppi.expenses) {
            const ry = r._yearMap.get(yr);
            if (ry) result += ry.value;
        }

        result += reduceTotalInvestsCached(yr, yearCache);
        result += reduceDeficitRecoveryCached(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache);
        result += year.tpoSpending || 0;

        const loan = loanMap.get(yr);
        if (loan) result += loan.reimbourse;

        return result;
    };

    self.computeTotalPpiForYear = (year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache) => {
        const yr = year.year;
        if (ppiTotalCache.has(yr)) return ppiTotalCache.get(yr);

        const revenue = computeTotalRevenueForYear(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache);
        const spending = computeTotalSpendingForYear(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, la, ppiTotalCache);
        const total = revenue - spending;
        ppiTotalCache.set(yr, total);
        return total;
    };

    // Cached CAF Brute — avoid re-computing for the same (type, year)
    self._cafBruteCache = {};

    self.getCachedCAFBrute = (type, year, la, operations, loanMap) => {
        const resolvedType = year < operations.scenario.year ? "retrospective" : type;
        const cacheKey = `${resolvedType}-${year}`;
        if (self._cafBruteCache[cacheKey] !== undefined) return self._cafBruteCache[cacheKey];
        const result = self._aggregateCAFBrute(resolvedType, year, la, operations, loanMap);
        self._cafBruteCache[cacheKey] = result;
        return result;
    };

    self._selfFinancingCache = {};

    self.getCachedSelfFinancing = (year, ppi, loans, priorities, current_year, scenario, la) => {
        const cacheKey = `${scenario ? scenario.id : "no-scenario"}-${year.year}`;
        if (self._selfFinancingCache[cacheKey] !== undefined) {
            return self._selfFinancingCache[cacheKey];
        }

        const result = self._reduceSelfFinancing(
            year,
            ppi,
            loans,
            priorities,
            current_year,
            scenario,
            la
        );
        self._selfFinancingCache[cacheKey] = result;
        return result;
    };

    // ─── Main build function ───

    self._buildData = ({
        ppi,
        shownThemes,
        priorities,
        current_year,
        as_responsible,
        shownGrants,
        currentLocalAuthority,
        scenario,
        loans,
    }) => {
        // Build all lookup maps once
        const loanMap = _buildLoanMap(loans);
        const ppiYearMap = _buildPpiYearMap(ppi.years);
        const crdYearMap = _buildCrdYearMap(currentLocalAuthority);

        // Store observation map on la for use in _aggregate* functions
        currentLocalAuthority._obsMap = _buildObservationMap(currentLocalAuthority.observations);
        currentLocalAuthority._crdYearMap = crdYearMap;
        currentLocalAuthority._sourceLoans = loans;

        const filteredProjects = _buildFilteredProjects(ppi, priorities);
        _buildProjectYearMaps(filteredProjects);
        _buildRevenueExpenseMaps(ppi.revenues);
        _buildRevenueExpenseMaps(ppi.expenses);

        const filteredYears = ppi.years.filter((y) => y.year >= current_year);
        // Pre-compute aggregates for ALL ppi years (not just filtered),
        // because previous surplus (N-1) and FCTVA (N-1/N-2) need earlier years
        const yearCache = _precomputeYearAggregates(ppi.years, ppi, filteredProjects, loanMap);

        // Cache for totalPpi per year (avoids recursive recomputation)
        const ppiTotalCache = new Map();

        var data = {};

        // Pre-compute all year-level values in a single pass
        const yearCount = filteredYears.length;

        // Initialize all data arrays
        const keys = [
            "FCTVA", "FCTVA_RETRIEVE", "REVENUE", "PREVIOUS_SURPLUS", "SELF_FINANCING",
            "FCTVA_VALUE", "PLANNING_FEE", "AWARDED_GRANT", "AWARDED_GRANT_DETAIL",
            "ASKED_GRANT", "ASKED_GRANT_DETAIL", "LOANS", "SALE_OF_HERITAGE",
            "REVENUE_THIRD_PARTIES_OPERATION", "NEW_REVENUE", "TOTAL_REVENUE",
            "SPENDING", "DEFICIT_RECOVERY", "LOAN_REFUND", "INVESTMENTS",
            "TOTAL_INVESTMENT_FCTVA", "TOTAL_INVESTMENT_NO_FCTVA", "TOTAL_INVESTMENT",
            "SPENDING_THIRD_PARTIES_OPERATION", "NEW_SPENDING", "TOTAL_SPENDING",
            "ANNUAL_PPI_TOTAL", "BALANCED_PPI", "RATIOS", "BRUTE_CAF", "CRD",
            "CAF_DEVELARGING", "DEBT_REFUND_PERCENTILE", "INVESTMENT_NET_CAF"
        ];
        for (const k of keys) {
            data[k] = new Array(yearCount);
        }

        for (let index = 0; index < yearCount; index++) {
            const year = filteredYears[index];
            const yr = year.year;
            const loan = loanMap.get(yr);
            const crdyear = crdYearMap.get(yr);
            const yc = yearCache.get(yr);

            // Simple properties
            data["FCTVA"][index] = (year.fctva || ppi.fctva) / 100;

            let fctvaRetrieveVal = year.fctvaRetrieve;
            if (fctvaRetrieveVal == null || fctvaRetrieveVal == undefined) fctvaRetrieveVal = ppi.fctvaRetrieve;
            const fctvaMapping = { 0: "N", 1: "N+1", 2: "N+2" };
            data["FCTVA_RETRIEVE"][index] = fctvaMapping[fctvaRetrieveVal];

            data["REVENUE"][index] = undefined; // separator row
            data["SPENDING"][index] = undefined;
            data["INVESTMENTS"][index] = undefined;
            data["NEW_REVENUE"][index] = undefined;
            data["NEW_SPENDING"][index] = undefined;
            data["RATIOS"][index] = undefined;
            data["AWARDED_GRANT_DETAIL"][index] = undefined;
            data["ASKED_GRANT_DETAIL"][index] = undefined;

            data["PLANNING_FEE"][index] = year.planningFee != null ? year.planningFee : ppi.planningFee;
            data["SALE_OF_HERITAGE"][index] = year.saleOfHeritage != null ? year.saleOfHeritage : ppi.saleOfHeritage;
            data["REVENUE_THIRD_PARTIES_OPERATION"][index] = year.tpoRevenue != null ? year.tpoRevenue : ppi.tpoRevenue;
            data["SPENDING_THIRD_PARTIES_OPERATION"][index] = year.tpoSpending != null ? year.tpoSpending : ppi.tpoSpending;

            // CAF Brute: use the exact legacy computation path and cache the result.
            const cafBrute = getCachedSelfFinancing(
                year,
                ppi,
                loans,
                priorities,
                current_year,
                scenario,
                currentLocalAuthority
            );
            data["SELF_FINANCING"][index] = cafBrute;

            // Previous surplus
            const prevSurplus = reducePreviousSurplusCached(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, currentLocalAuthority, ppiTotalCache);
            data["PREVIOUS_SURPLUS"][index] = prevSurplus == 0 ? "" : Math.round(prevSurplus);

            // FCTVA value
            const fctvaVal = reduceFctvaCached(year, ppi, ppiYearMap, yearCache);
            data["FCTVA_VALUE"][index] = fctvaVal === 0 ? "" : Math.round(fctvaVal);

            // Grants
            const gAwarded = yc ? yc.grantsAwarded : 0;
            data["AWARDED_GRANT"][index] = gAwarded === 0 ? "" : Math.round(gAwarded);
            const gAsked = yc ? yc.grantsAsked : 0;
            data["ASKED_GRANT"][index] = gAsked == 0 ? "" : Math.round(gAsked);

            // Emprunts : nominal contracté dans l'année. Un emprunt n'apparaît donc que sur sa
            // première année, y compris en dette simplifiée (le CRD reste sur la ligne « CRD »).
            const loanResult = loan ? loan.total : 0;
            data["LOANS"][index] = loanResult == 0 ? "" : Math.round(loanResult);

            // Total revenue
            const totalRevenue = computeTotalRevenueForYear(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, currentLocalAuthority, ppiTotalCache);
            data["TOTAL_REVENUE"][index] = totalRevenue == 0 ? "" : Math.round(totalRevenue);

            // Deficit recovery
            const deficitRecovery = reduceDeficitRecoveryCached(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, currentLocalAuthority, ppiTotalCache);
            data["DEFICIT_RECOVERY"][index] = deficitRecovery == 0 ? "" : Math.round(deficitRecovery);

            // Loan refund
            let loanRefund = 0;
            if (currentLocalAuthority.isDebtSimplified) {
                var empruntReel = 0;
                if (year.loanDebtAmount != null) {
                    empruntReel = year.loanDebtAmount;
                } else {
                    const loan = loans.find((l) => l.year == year.year);
                    if (loan && loan.reimbourse != null) {
                        empruntReel = loan.reimbourse;
                    }
                }
                const empruntSimule = crdyear ? (crdyear.amount_simulated_loan_repayment != null ? crdyear.amount_simulated_loan_repayment : 0) : 0;
                loanRefund = empruntReel + empruntSimule;
            } else {
                if (loan) loanRefund = loan.reimbourse;
            }
            data["LOAN_REFUND"][index] = loanRefund == 0 ? "" : Math.round(loanRefund);

            // Investments
            const totalInvFctva = yc ? yc.totalInvestsFctva : 0;
            const totalInv = yc ? yc.totalInvests : 0;
            data["TOTAL_INVESTMENT_FCTVA"][index] = totalInvFctva;
            data["TOTAL_INVESTMENT_NO_FCTVA"][index] = totalInv - totalInvFctva;
            data["TOTAL_INVESTMENT"][index] = totalInv;

            // Total spending
            const totalSpending = computeTotalSpendingForYear(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, currentLocalAuthority, ppiTotalCache);
            data["TOTAL_SPENDING"][index] = totalSpending == 0 ? "" : Math.round(totalSpending);

            // Annual PPI total
            const totalPpi = computeTotalPpiForYear(year, ppi, ppiYearMap, loanMap, yearCache, current_year, scenario, currentLocalAuthority, ppiTotalCache);
            data["ANNUAL_PPI_TOTAL"][index] = totalPpi;

            // Balanced PPI
            data["BALANCED_PPI"][index] = totalPpi + (cafBrute || 0);

            // Brute CAF
            data["BRUTE_CAF"][index] = cafBrute;

            // CRD
            let crdResult = 0;
            if (currentLocalAuthority.isDebtSimplified) {
                if (crdyear) {
                    crdResult = (crdyear.amount != null ? crdyear.amount : 0) + (crdyear.amount_simulated_loan_crd != null ? crdyear.amount_simulated_loan_crd : 0);
                }
            } else {
                if (loan) crdResult = loan.remaining;
            }
            data["CRD"][index] = crdResult == 0 ? "" : Math.round(crdResult);

            // CAF Develarging
            let cafDevelarging = 0;
            if (cafBrute) {
                if (currentLocalAuthority.isDebtSimplified) {
                    if (crdyear) {
                        const resultcrd = (crdyear.amount != null ? crdyear.amount : 0) + (crdyear.amount_simulated_loan_crd != null ? crdyear.amount_simulated_loan_crd : 0);
                        cafDevelarging = resultcrd != 0 ? resultcrd / cafBrute : 0;
                    }
                } else if (loan) {
                    cafDevelarging = loan.remaining != null ? loan.remaining / cafBrute : 0;
                }
            }
            data["CAF_DEVELARGING"][index] = cafDevelarging;

            // Debt refund percentile
            let debtRefund = 0;
            if (cafBrute) {
                if (currentLocalAuthority.isDebtSimplified) {
                    var empruntReelRefund = 0;
                    if (year.loanDebtAmount != null) {
                        empruntReelRefund = year.loanDebtAmount;
                    } else {
                        const loanForRefund = loans.find((l) => l.year == year.year);
                        if (loanForRefund && loanForRefund.reimbourse != null) {
                            empruntReelRefund = loanForRefund.reimbourse;
                        }
                    }
                    const rembSimule = crdyear ? (crdyear.amount_simulated_loan_repayment != null ? crdyear.amount_simulated_loan_repayment : 0) : 0;
                    const totalRefund = empruntReelRefund + rembSimule;
                    debtRefund = totalRefund != 0 ? totalRefund / cafBrute : 0;
                } else if (loan) {
                    debtRefund = loan.reimbourse != null ? loan.reimbourse / cafBrute : 0;
                }
            }
            data["DEBT_REFUND_PERCENTILE"][index] = debtRefund == 0 ? "" : debtRefund;

            // Investment net CAF
            let investNetCaf = 0;
            if (currentLocalAuthority.isDebtSimplified) {
                var empruntReelNet = 0;
                if (year.loanDebtAmount != null) {
                    empruntReelNet = year.loanDebtAmount;
                } else {
                    const loanForNet = loans.find((l) => l.year == year.year);
                    if (loanForNet && loanForNet.reimbourse != null) {
                        empruntReelNet = loanForNet.reimbourse;
                    }
                }
                const rembSimuleNet = crdyear ? (crdyear.amount_simulated_loan_repayment != null ? crdyear.amount_simulated_loan_repayment : 0) : 0;
                investNetCaf = cafBrute - (empruntReelNet + rembSimuleNet);
            } else {
                investNetCaf = cafBrute - (loan ? loan.reimbourse : 0);
            }
            data["INVESTMENT_NET_CAF"][index] = investNetCaf == 0 ? "" : Math.round(investNetCaf);
        }

        const projectsData = InvestmentsHelper(
            ppi,
            shownThemes,
            priorities,
            current_year
        );

        return { data, projectsData };
    };

    // ─── Legacy functions kept for _aggregate* calls ───

    self.aggregateGrants = (awarded, year, ppi, loans, priorities) => {
        var result = 0,
            amount;
        ppi.investment_themes.map((t) => {
            t.projects.map((p) => {
                if (
                    p.visible &&
                    (priorities.length == 0 || priorities.indexOf(p.priority) !== -1)
                ) {
                    p.grants
                        .filter((g) => g.awarded == awarded)
                        .map((g) => {
                            amount = g.years.find((y) => y.year == year.year).amount;
                            if (amount) {
                                result += amount;
                            }
                        });
                }
            });
            if (t.themes.length > 0) {
                t.themes.map((t) => {
                    t.projects.map((p) => {
                        if (
                            p.visible &&
                            (priorities.length == 0 || priorities.indexOf(p.priority) !== -1)
                        ) {
                            p.grants
                                .filter((g) => g.awarded == awarded)
                                .map((g) => {
                                    amount = g.years.find((y) => y.year == year.year).amount;
                                    if (amount) {
                                        result += amount;
                                    }
                                });
                        }
                    });
                });
            }
        });
        return result;
    };
    self.reduceTotalInvests = (
        year,
        ppi,
        loans,
        priorities,
        current_year,
        fctvaOnly = false
    ) => {
        var result = 0;
        ppi.investment_themes.map((t) => {
            t.projects.map((p) => {
                if (
                    p.visible &&
                    (priorities.length == 0 || priorities.indexOf(p.priority) !== -1) &&
                    (!fctvaOnly || p.fctva)
                ) {
                    result += p.years.find((y) => y.year == year.year).amount;
                }
            });
            t.themes.map((st) => {
                st.projects.map((p) => {
                    if (
                        p.visible &&
                        (priorities.length == 0 || priorities.indexOf(p.priority) !== -1) &&
                        (!fctvaOnly || p.fctva)
                    ) {
                        result += p.years.find((y) => y.year == year.year).amount;
                    }
                });
            });
        });
        return result;
    };

    self.reduceFctva = (year, ppi, loans, priorities) => {
        var result = 0;
        var fctvaRetrieve;
        var toRetrieve = ppi.years.filter((y) => {
            fctvaRetrieve = y.fctvaRetrieve;
            if (fctvaRetrieve == null || fctvaRetrieve == undefined) {
                fctvaRetrieve = ppi.fctvaRetrieve;
            }
            return fctvaRetrieve + y.year === year.year;
        });
        var fctva;
        toRetrieve.map((y) => {
            if (y.fctvaRetrieve === 0) {
                fctva = ppi.years.find((py) => py.year === year.year).fctva / 100;
                if (fctva == null || fctva == undefined) {
                    fctva = ppi.fctva / 100
                }
                if (fctva === null || fctva === undefined || fctva === 0) {
                    fctva = 0.164
                }
                result +=
                    reduceTotalInvests(year, ppi, loans, priorities, null, true) * fctva;
            } else {
                fctva = ppi.years.find((py) => py.year === y.year).fctva / 100;

                if (fctva == null || fctva == undefined) {
                    fctva = ppi.fctva / 100
                }
                if (fctva === null || fctva === undefined || fctva === 0) {
                    fctva = 0.164
                }
                result +=
                    reduceTotalInvests(y, ppi, loans, priorities, null, true) * fctva;
            }
        });
        return result;
    };

    self.reducePreviousSurplus = (
        year,
        ppi,
        loans,
        priorities,
        current_year,
        scenario,
        la
    ) => {
        if (ppi.years.some(o => o.year == year.year - 1)) {
            const ppiLastYear = ppi.years.find(o => o.year == year.year - 1);
            if (ppiLastYear.investResult != null)
                return ppiLastYear.investResult && ppiLastYear.investResult > 0 ? ppiLastYear.investResult : 0;
        }
        if (ppi.years[0].year == year.year) {
            return ppi.years[0].previousSurplus || 0;
        } else {
            var result = 0;
            if (ppi.years.find((y) => y.year == year.year - 1)) {
                result = reduceTotalPpi(
                    ppi.years.find((y) => y.year == year.year - 1),
                    ppi,
                    loans,
                    priorities,
                    current_year,
                    scenario,
                    la
                );
            }
            return result > 0 ? result : 0;
        }
    };

    self.reduceTotalRevenue = (
        year,
        ppi,
        loans,
        priorities,
        current_year,
        scenario,
        la
    ) => {
        var result = 0;
        ppi.revenues.map((r) => {
            result += r.years.find((y) => y.year == year.year).value;
        });
        result += year.saleOfHeritage || 0;
        result += year.tpoRevenue || 0;
        result += year.planningFee || 0;

        var selfFinancing = year.selfFinancing;
        if (scenario) {
            selfFinancing = self._aggregateCAFBrute(
                "prospective",
                year.year,
                la,
                self._getOperations(scenario, ppi, la),
                loans
            );
        }

        const previousSurplus = reducePreviousSurplus(
            year,
            ppi,
            loans,
            priorities,
            current_year,
            scenario,
            la
        );
        const fctva = reduceFctva(year, ppi, loans, priorities);
        const grantsAwarded = aggregateGrants(true, year, ppi, loans, priorities);
        const grantsAsked = aggregateGrants(false, year, ppi, loans, priorities);

        result += selfFinancing || 0;
        result += previousSurplus;
        result += fctva;
        result += grantsAwarded;
        result += grantsAsked;

        let loan = loans.find((l) => l.year == year.year);
        if (loan) result += loan.total;

        return result;
    };

    self.reduceDeficitRecovery = (
        year,
        ppi,
        loans,
        priorities,
        current_year,
        scenario,
        la
    ) => {
        if (ppi.years.some(o => o.year == year.year - 1)) {
            const ppiLastYear = ppi.years.find(o => o.year == year.year - 1);
            if (ppiLastYear.investResult != null)
                return ppiLastYear.investResult && ppiLastYear.investResult < 0 ? Math.abs(ppiLastYear.investResult) : 0;
        }
        if (ppi.years[0].year == year.year) {
            return ppi.years[0].deficitRecovery || 0;
        } else {
            var result = 0;
            if (ppi.years.find((y) => y.year == year.year - 1)) {
                result = reduceTotalPpi(
                    ppi.years.find((y) => y.year == year.year - 1),
                    ppi,
                    loans,
                    priorities,
                    current_year,
                    scenario,
                    la
                );
            }
            return result < 0 ? result * -1 : 0;
        }
    };

    self.reduceTotalSpending = (
        year,
        ppi,
        loans,
        priorities,
        current_year,
        scenario,
        la
    ) => {
        var result = 0;
        ppi.expenses.map((r) => {
            result += r.years.find((y) => y.year == year.year).value;
        });
        result += reduceTotalInvests(year, ppi, loans, priorities);
        result += reduceDeficitRecovery(
            year,
            ppi,
            loans,
            priorities,
            current_year,
            scenario,
            la
        );
        result += year.tpoSpending || 0;

        let loan = loans.find((l) => l.year == year.year);
        if (loan) result += loan.reimbourse;

        return result;
    };

    self.reduceTotalPpi = (
        year,
        ppi,
        loans,
        priorities,
        current_year,
        scenario,
        la
    ) => {
        return (
            reduceTotalRevenue(year, ppi, loans, priorities, current_year, scenario, la) - reduceTotalSpending(year, ppi, loans, priorities, current_year, scenario, la)
        );
    };

    self._aggregateRealOperations = (type, year, la, operations, receipts) => {
        var orderOperationsTotal = self._aggregateOrderOperationsTotal(
            type,
            year,
            la,
            operations,
            receipts,
            receipts ? ["042", "043"] : ["042", "043", "023"]
        );
        var operationsTotal = self._aggregateOperationsTotal(
            type,
            year,
            la,
            operations,
            receipts
        );
        return operationsTotal - orderOperationsTotal;
    };

    self._aggregateCAFBrute = (type, year, la, operations, loansOrMap) => {
        if (year < operations.scenario.year) type = "retrospective";

        var result = self._aggregateOperationsTotal(type, year, la, operations, true) - self._aggregateOperationsTotal(type, year, la, operations, false, loansOrMap);
        result -= self._aggregateOrderOperationsTotal(type, year, la, operations, true);
        result += self._aggregateOrderOperationsTotal(type, year, la, operations, false);
        return result;
    };

    self.orderNumbers = ["68*", "78*", "675*", "676*", "775*", "776*", "777*"];

    self._orderAccountsCache = {};

    self._getOrderOperationAccounts = (la, receipts, customNumbers) => {
        const cacheKey = `${receipts}-${customNumbers ? customNumbers.join(',') : ''}`;
        if (self._orderAccountsCache[cacheKey]) return self._orderAccountsCache[cacheKey];

        let ok;
        let numbers = customNumbers ? customNumbers : orderNumbers;

        const result = la.accounts.filter((laca) => {
            ok = false;
            if (receipts == undefined || receipts == null || (receipts && laca.account.receipts) || (!receipts && laca.account.expense)) {
                for (const on of numbers) {
                    if (ok) continue;

                    if (on.indexOf("*") == -1) {
                        ok = laca.chapter && laca.chapter.number == on;
                    } else {
                        ok = laca.account.number.startsWith(on.substring(0, on.length - 1));
                    }
                }
            }
            return ok;
        });
        self._orderAccountsCache[cacheKey] = result;
        return result;
    };

    self._aggregateOrderOperationsTotal = (
        type,
        year,
        la,
        operations,
        receipts,
        customNumbers
    ) => {
        var result = 0, obs;
        const obsMap = la._obsMap;
        for (const laca of _getOrderOperationAccounts(la, receipts, customNumbers)) {
            if (type == "retrospective") {
                const obsKey = `${year}-${laca.account.id}`;
                obs = obsMap ? obsMap.get(obsKey) : la.observations.find((o) => o.year == year && o.account.id == laca.account.id);
                if (obs) result += obs.amount;
            } else {
                result += operations.findValue(year, laca.account.id, laca.chapter ? laca.chapter.id : null, receipts);
            }
        }

        return result;
    };

    self._operationAccountsCache = {};

    self._aggregateOperationsTotal = (
        type,
        year,
        la,
        operations,
        receipts,
        loansOrMap = null
    ) => {
        // Resolve loan — support both array and Map
        let loan = { interests: 0 };
        if (!receipts && loansOrMap) {
            if (loansOrMap instanceof Map) {
                loan = loansOrMap.get(year) || { interests: 0 };
            } else if (Array.isArray(loansOrMap)) {
                loan = loansOrMap.find((l) => l.year === year) || { interests: 0 };
            }
        }

        // Cache filtered accounts
        const accountsCacheKey = `${receipts}`;
        let accounts;
        if (receipts !== undefined && receipts !== null) {
            if (self._operationAccountsCache[accountsCacheKey]) {
                accounts = self._operationAccountsCache[accountsCacheKey];
            } else {
                accounts = la.accounts.filter((laca) => laca.account.operation && ((receipts && laca.account.receipts) || (!receipts && laca.account.expense)));
                self._operationAccountsCache[accountsCacheKey] = accounts;
            }
        } else {
            accounts = la.accounts;
        }

        let result = 0;
        const obsMap = la._obsMap;

        for (const laca of accounts) {
            if (type == "retrospective") {
                const obsKey = `${year}-${laca.account.id}`;
                const obs = obsMap ? obsMap.get(obsKey) : la.observations.find((o) => o.year == year && o.account.id == laca.account.id);
                if (obs) result += obs.amount;
            } else {
                if ("66111" === laca.account.number) {
                    result += loan ? loan.interests : 0;
                    if (la.isDebtSimplified && la._crdYearMap) {
                        const crdYear = la._crdYearMap.get(year);
                        if (crdYear && crdYear.amount_simulated_loan_interest != null) {
                            result += crdYear.amount_simulated_loan_interest;
                        }
                    }
                } else {
                    result += operations.findValue(year, laca.account.id, laca.chapter ? laca.chapter.id : null, receipts);
                }
            }
        }

        return result;
    };

    self._reduceSelfFinancing = (
        year,
        ppi,
        loans,
        priorities,
        current_year,
        scenario,
        la
    ) => {
        if (scenario) {
            return self._aggregateCAFBrute("prospective", year.year, la, self._getOperations(scenario, ppi, la), loans);
        } else {
            return year.selfFinancing;
        }
    };

    self.operations = {};

    self._getOperations = (scenario, ppi, la) => {
        if (!self.operations[`${scenario.id}`]) {
            self.operations[`${scenario.id}`] = new Operations(scenario, ppi, la);
        }
        return self.operations[`${scenario.id}`];
    };

    function Operations(scenario, ppi, localAuthority) {
        this.scenario = scenario;
        this.ppi = ppi;
        this.localAuthority = localAuthority;

        this.values = {};

        // Pre-index scenario data for fast lookup
        this._scenarioYearMap = new Map();
        if (scenario.years) {
            for (const y of scenario.years) {
                if (!this._scenarioYearMap.has(y.year)) {
                    this._scenarioYearMap.set(y.year, y);
                }
                if (!y._accountMap) {
                    y._accountMap = new Map();
                    for (const a of y.accounts) {
                        const key = `${a.account_id}-${a.receipts}`;
                        if (!y._accountMap.has(key)) {
                            y._accountMap.set(key, a);
                        }
                    }
                }
            }
        }

        this._scenarioAccountMap = new Map();
        if (scenario.accounts) {
            for (const a of scenario.accounts) {
                const key = `${a.account_id}-${a.receipts}`;
                if (!this._scenarioAccountMap.has(key)) {
                    this._scenarioAccountMap.set(key, a);
                }
            }
        }

        this._scenarioChapterMap = new Map();
        if (scenario.chapters) {
            for (const c of scenario.chapters) {
                const key = `${c.chapter_id}-${c.receipts}`;
                if (!this._scenarioChapterMap.has(key)) {
                    this._scenarioChapterMap.set(key, c);
                }
            }
        }

        // Pre-index observations
        this._obsMap = localAuthority._obsMap || _buildObservationMap(localAuthority.observations);

        // Pre-build induced index: Map<"year-accountId", induced[]>
        this._inducedIndex = new Map();
        const allVisibleProjects = [];
        for (const theme of ppi.investment_themes) {
            for (const p of theme.projects) {
                if (p.visible) allVisibleProjects.push(p);
            }
            for (const st of theme.themes) {
                for (const p of st.projects) {
                    if (p.visible) allVisibleProjects.push(p);
                }
            }
        }
        for (const project of allVisibleProjects) {
            for (const ind of project.induceds) {
                const key = `${ind.year}-${ind.induced.id}`;
                if (!this._inducedIndex.has(key)) {
                    this._inducedIndex.set(key, []);
                }
                this._inducedIndex.get(key).push({ ...ind, project });
            }
        }

        this.findValue = (
            year,
            account_id,
            chapter_id,
            receipts,
            previous_scenario = null
        ) => {
            const cacheKey = `${year}-${account_id}-${chapter_id}-${receipts}-${previous_scenario ? previous_scenario.id : ""}`;
            if (this.values[cacheKey] !== undefined) {
                return this.values[cacheKey];
            }

            var value = 0;
            var scenarioYear, scenarioYearAccount, chapter, account, obs;
            var manageInduceds = true;
            if (year == this.scenario.year - 1) {
                if (previous_scenario) {
                    value = new Operations(previous_scenario, this.ppi, this.localAuthority).findValue(year, account_id, chapter_id, receipts);
                } else {
                    obs = this.localAuthority.observations.find((o) => o.year == year && o.account.id == account_id);
                    if (obs) value = obs.amount;
                }
            } else {
                value = this.findValue(year - 1, account_id, chapter_id, receipts, previous_scenario);

                if ((scenarioYear = this.scenario.years.find((y) => y.year == year))
                    && (scenarioYearAccount = scenarioYear.accounts.find((a) => a.account_id == account_id && (receipts == undefined || a.receipts == receipts))) && scenarioYearAccount.amount !== null) {
                    if (scenarioYearAccount.percentile) {
                        value = this.findValue(year - 1, account_id, chapter_id, receipts, previous_scenario);
                        value += (value * scenarioYearAccount.amount) / 100;
                    } else {
                        value = scenarioYearAccount.amount;
                        manageInduceds = false;
                    }
                } else {
                    if (account = this.scenario.accounts.find((a) => a.account_id == account_id && (receipts == undefined || a.receipts == receipts))) {
                        value += (value * account.amount) / 100;
                    } else {
                        if (chapter = this.scenario.chapters.find((c) => c.chapter_id == chapter_id && (receipts == undefined || c.receipts == receipts))) {
                            value += (value * chapter.amount) / 100;
                        }
                    }
                }
            }

            value = parseFloat(value);

            if (manageInduceds) {
                const obsKey = `${year}-${account_id}`;
                if (!this._obsMap.has(obsKey)) {
                    const inducedKey = `${year}-${account_id}`;
                    const induceds = this._inducedIndex.get(inducedKey);
                    if (induceds) {
                        for (const i of induceds) {
                            value += (i.gain ? i.gain : 0) - (i.loss ? i.loss : 0);
                        }
                    }
                }
            }

            this.values[cacheKey] = value;
            return value;
        };

        this.findInduceds = (year, account_id) => {
            const key = `${year}-${account_id}`;
            return this._inducedIndex.get(key) || [];
        };
    }

    self.InvestmentsHelper = (ppi, shownThemes, priorities, current_year) => {
        var data = [];
        var rest = 0;
        var noPriorityFilter = priorities.length === 0;
        ppi.investment_themes.map((investment_theme) => {
            data.push(
                _reduceThemeProjectAmounts(
                    investment_theme,
                    ppi,
                    current_year,
                    priorities
                )
            );
            data[data.length - 1].unshift(investment_theme.name);
            investment_theme.themes.map((subtheme) => {
                data.push(
                    _reduceSubThemeProjectAmounts(subtheme, ppi, current_year, priorities)
                );
                data[data.length - 1].unshift(subtheme.name);
                subtheme.projects.map((project) => {
                    if (
                        noPriorityFilter ||
                        priorities.indexOf(project.priority) !== -1
                    ) {
                        rest = 0;
                        project.years.map((y) => {
                            rest += y.amount ? y.amount : 0;
                        });
                        rest = project.budget - rest;
                        project.grants.map((grant) => {
                            grant.years.map((year) => {
                                rest += year.amount ? year.amount : 0;
                            });
                            rest -= grant.budget;
                        });
                        data.push(
                            project.years
                                .filter((y) => y.year >= current_year)
                                .map((y) => y.amount)
                        );
                        data[data.length - 1].unshift(project.name);
                    }
                });
            });

            investment_theme.projects.map((project) => {
                if (
                    noPriorityFilter ||
                    priorities.indexOf(project.priority) !== -1
                ) {
                    rest = 0;
                    project.years.map((y) => {
                        rest += y.amount ? y.amount : 0;
                    });
                    rest = project.budget - rest;
                    project.grants.map((grant) => {
                        grant.years.map((year) => {
                            rest += year.amount ? year.amount : 0;
                        });
                        rest -= grant.budget;
                    });
                    data.push(
                        project.years
                            .filter((y) => y.year >= current_year)
                            .map((y) => y.amount)
                    );
                    data[data.length - 1].unshift(project.name);
                }
            });
        });

        return data;
    };

    self._reduceThemeProjectAmounts = (theme, ppi, current_year, priorities) => {
        return ppi.years
            .filter((y) => y.year >= current_year)
            .map((y) => {
                var amount = _reduceSubThemeProjectAmount(y, theme, priorities);
                theme.themes.map((t) => {
                    amount += _reduceSubThemeProjectAmount(y, t, priorities);
                });
                return amount;
            });
    };

    self._reduceSubThemeProjectAmounts = (
        subtheme,
        ppi,
        current_year,
        priorities
    ) => {
        return ppi.years
            .filter((y) => y.year >= current_year)
            .map((y) => _reduceSubThemeProjectAmount(y, subtheme, priorities));
    };

    self._reduceSubThemeProjectAmount = (year, theme, priorities) => {
        var r = 0;
        theme.projects.map((p) => {
            if (
                p.visible &&
                (priorities.length == 0 || priorities.indexOf(p.priority) !== -1)
            ) {
                const py = p._yearMap ? p._yearMap.get(year.year) : p.years.find((y) => y.year == year.year);
                if (py) r += py.amount;
            }
        });
        return r;
    };
};



// WEBPACK FOOTER //
// ./src/component/ppi/ppi-detail/ppi-worker.js